import * as React from "react";
import { useEffect, useMemo } from "react";
import { Control, useController, useWatch } from "react-hook-form";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { SxProps } from "@mui/material/styles";

import { includes } from "lodash";

import CheckboxFormField from "components/checkbox-form-field";
import PreferredValueWarning from "components/preferred-value-warning/preferred-value-warning.component";
import { GROUP_AUDIOLOGISTS } from "constants/staff";
import { useGetStaffQuery } from "services/staff";
import {
  MinimalStaffMember,
  StaffFilters,
  StaffMember
} from "services/types/staff.type";

import useStaffMemberSchedule from "../../scheduler/use-staff-member-schedule.hook";
import { AppointmentFormValues } from "../appointment-form.component";

type StaffFieldProps = {
  control: Control<AppointmentFormValues, object>;
  showAvailableWarning?: boolean;
  setShowAvailableWarning?: (value: boolean) => void;
  showActiveOnly?: boolean;
  preferredProvider?: StaffMember;
  isGlobalPracticeEvent?: boolean;
  disabled?: boolean;
  sx?: SxProps;
};

export default function StaffField({
  control,
  showAvailableWarning = false,
  setShowAvailableWarning,
  showActiveOnly = false,
  isGlobalPracticeEvent = false,
  preferredProvider,
  disabled = false,
  sx
}: StaffFieldProps) {
  const [staffMember, appointmentType, startTime, endTime, clinic] = useWatch({
    control,
    name: [
      "staff_member",
      "appointment_type",
      "start_time",
      "end_time",
      "clinic"
    ]
  });
  const { isAvailable, isSuccess } = useStaffMemberSchedule({
    staffMemberId: staffMember?.id || null,
    clinicId: clinic?.id,
    appointmentStartDate: startTime,
    appointmentEndDate: endTime
  });
  const staffFilters: StaffFilters = useMemo(
    () => ({
      clinicIds: !isGlobalPracticeEvent && clinic?.id ? [clinic.id] : [],
      groupName: appointmentType?.is_audiologists_only
        ? GROUP_AUDIOLOGISTS
        : undefined,
      is_active: showActiveOnly || null
    }),
    [clinic, appointmentType, showActiveOnly, isGlobalPracticeEvent]
  );
  const { data: staff = [], isLoading } = useGetStaffQuery(staffFilters);
  const {
    field: { onChange, ...restField },
    formState: { errors }
  } = useController({
    name: "staff_member",
    control,
    rules: {
      required: "Staff is required"
    }
  });

  useEffect(() => {
    setShowAvailableWarning &&
      setShowAvailableWarning(isSuccess && !isAvailable && !!staffMember?.id);
  }, [isSuccess, isAvailable, staffMember?.id, setShowAvailableWarning]);

  useEffect(() => {
    if (!isLoading && staffMember?.id) {
      const isCurrentStaffMemberValidChoice = includes(
        staff.map(({ id }) => id),
        staffMember?.id
      );
      if (!isCurrentStaffMemberValidChoice) {
        onChange(null);
        restField.onBlur();
      }
    }
  }, [staff, staffMember, isLoading, onChange, restField]);
  const staffOptions = useMemo(() => {
    return (staff as MinimalStaffMember[]).filter(({ id, is_active }) => {
      return is_active || id === staffMember?.id;
    });
  }, [staff, staffMember]);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={sx}
      >
        <PeopleOutlinedIcon sx={{ mt: 1.5, mr: 1.5 }} color="action" />
        <Stack direction="row" spacing={2} width="100%">
          <Box sx={{ minWidth: showAvailableWarning ? "290px" : "100%" }}>
            <Autocomplete
              disablePortal
              options={staffOptions}
              loading={isLoading}
              fullWidth
              popupIcon={<ExpandMoreOutlinedIcon />}
              getOptionLabel={({ fullName }) => fullName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  {...(Boolean(errors?.staff_member?.message)
                    ? {
                        error: true,
                        helperText: errors?.staff_member?.message
                      }
                    : {})}
                  label="Staff"
                  sx={{
                    textDecoration:
                      staffMember?.is_active === false
                        ? "line-through"
                        : undefined
                  }}
                  disabled={disabled}
                />
              )}
              onChange={(_, data) => {
                onChange(data);
                restField.onBlur();
              }}
              {...restField}
              disabled={disabled}
            />
          </Box>
          {showAvailableWarning && (
            <FormControlLabel
              label="Allow to create appointment outside working hours"
              control={
                <CheckboxFormField
                  testId="should_create_schedule_for_staff"
                  name="should_create_schedule_for_staff"
                  control={control}
                />
              }
            />
          )}
        </Stack>
      </Box>

      <PreferredValueWarning
        preferredValueTitle="provider"
        patientPreferredValue={
          preferredProvider
            ? {
                id: preferredProvider?.id,
                name: preferredProvider?.fullName
              }
            : undefined
        }
        selectedValue={restField?.value}
        sx={{ pl: 4 }}
      />
    </Box>
  );
}
